import React, { useState, useCallback, useRef, useEffect } from 'react';
import { PageToggleButton } from '../../shared/PageToggleButton/PageToggleButton';
import './FilmPagination.css';

// Awasi destination themes for property-specific styling
export type DestinationVariant = 'default' | 'atacama' | 'patagonia' | 'iguazu' | 'mendoza' | 'santa-catarina';

// Size variants for different use cases
export type SizeVariant = 'small' | 'medium' | 'large';

// Color scheme variants for different backgrounds
export type ColorScheme = 'light' | 'dark';

interface FilmPaginationProps {
  currentPage: number;
  totalPages: number;
  onPageChange: (page: number) => void;
  
  // Enhanced theming props
  variant?: DestinationVariant;
  size?: SizeVariant;
  colorScheme?: ColorScheme;
  
  // Accessibility props
  ariaLabel?: string;
  
  // Performance and behavior props
  disabled?: boolean;
  showScrollToTop?: boolean;
  
  // Custom styling
  className?: string;
}

const FilmPagination: React.FC<FilmPaginationProps> = ({ 
  currentPage, 
  totalPages, 
  onPageChange,
  variant = 'default',
  size = 'medium',
  colorScheme = 'light',
  ariaLabel = 'Film collection pagination',
  disabled = false,
  showScrollToTop = true,
  className = ''
}) => {
  const [hoveredPage, setHoveredPage] = useState<number | null>(null);
  const [focusedPage, setFocusedPage] = useState<number | null>(null);
  const containerRef = useRef<HTMLDivElement>(null);

  const handlePageChange = useCallback((page: number) => {
    if (disabled || page === currentPage) return;
    
    onPageChange(page);
    
    // Smooth scroll to top of film grid if enabled
    if (showScrollToTop) {
      const filmGrid = document.querySelector('.film-grid');
      if (filmGrid) {
        filmGrid.scrollIntoView({ behavior: 'smooth', block: 'start' });
      }
    }
  }, [onPageChange, disabled, currentPage, showScrollToTop]);
  
  // Keyboard navigation handler
  const handleKeyDown = useCallback((event: React.KeyboardEvent, page: number) => {
    switch (event.key) {
      case 'Enter':
      case ' ': // Space key
        event.preventDefault();
        handlePageChange(page);
        break;
      case 'ArrowLeft':
        event.preventDefault();
        if (page > 1) {
          setFocusedPage(page - 1);
          // Focus the previous button
          const prevButton = containerRef.current?.querySelector(`[data-page="${page - 1}"]`) as HTMLButtonElement;
          prevButton?.focus();
        }
        break;
      case 'ArrowRight':
        event.preventDefault();
        if (page < totalPages) {
          setFocusedPage(page + 1);
          // Focus the next button
          const nextButton = containerRef.current?.querySelector(`[data-page="${page + 1}"]`) as HTMLButtonElement;
          nextButton?.focus();
        }
        break;
      case 'Home':
        event.preventDefault();
        setFocusedPage(1);
        const firstButton = containerRef.current?.querySelector(`[data-page="1"]`) as HTMLButtonElement;
        firstButton?.focus();
        break;
      case 'End':
        event.preventDefault();
        setFocusedPage(totalPages);
        const lastButton = containerRef.current?.querySelector(`[data-page="${totalPages}"]`) as HTMLButtonElement;
        lastButton?.focus();
        break;
    }
  }, [handlePageChange, totalPages]);

  const getFrameSrc = useCallback((page: number): string | undefined => {
    const isActive = currentPage === page;
    const isHovered = hoveredPage === page;
    const isFocused = focusedPage === page;
    
    // Determine color based on variant and color scheme
    const getColor = () => {
      if (colorScheme === 'dark') return 'off-white';
      return 'awasi'; // Default to awasi color for all variants in light mode
    };
    
    const color = getColor();
    
    if (isActive) {
      return `/Buttons/Page Toggle Frames/state=selected, color=${color}.svg`;
    } else if (isHovered || isFocused) {
      return `/Buttons/Page Toggle Frames/state=hover, color=${color}.svg`;
    }
    return undefined;
  }, [currentPage, hoveredPage, focusedPage, colorScheme]);
  
  // Generate CSS classes for theming
  const getButtonClasses = useCallback((page: number) => {
    const baseClasses = ['film-pagination__button'];
    
    if (currentPage === page) {
      baseClasses.push('film-pagination__button--active');
    }
    
    if (disabled) {
      baseClasses.push('film-pagination__button--disabled');
    }
    
    if (variant !== 'default') {
      baseClasses.push(`film-pagination__button--${variant}`);
    }
    
    if (size !== 'medium') {
      baseClasses.push(`film-pagination__button--${size}`);
    }
    
    if (colorScheme === 'dark') {
      baseClasses.push('film-pagination__button--dark');
    }
    
    return baseClasses.join(' ');
  }, [currentPage, disabled, variant, size, colorScheme]);

  // Effect to manage focus state
  useEffect(() => {
    if (focusedPage !== null) {
      const timer = setTimeout(() => setFocusedPage(null), 100);
      return () => clearTimeout(timer);
    }
  }, [focusedPage]);
  
  const wrapperClasses = [
    'film-pagination-wrapper',
    variant !== 'default' ? `film-pagination-wrapper--${variant}` : '',
    size !== 'medium' ? `film-pagination-wrapper--${size}` : '',
    colorScheme === 'dark' ? 'film-pagination-wrapper--dark' : '',
    disabled ? 'film-pagination-wrapper--disabled' : '',
    className
  ].filter(Boolean).join(' ');
  
  const containerClasses = [
    'film-pagination',
    variant !== 'default' ? `film-pagination--${variant}` : '',
    size !== 'medium' ? `film-pagination--${size}` : '',
    colorScheme === 'dark' ? 'film-pagination--dark' : ''
  ].filter(Boolean).join(' ');

  return (
    <div className={wrapperClasses}>
      <nav 
        className={containerClasses}
        role="navigation"
        aria-label={ariaLabel}
        ref={containerRef}
      >
        {Array.from({ length: totalPages }, (_, i) => i + 1).map((page) => {
          const isActive = currentPage === page;
          
          return (
            <PageToggleButton
              key={page}
              number={page}
              isActive={isActive}
              onClick={() => handlePageChange(page)}
              color={colorScheme === 'dark' ? 'off-white' : 'awasi'}
              className={`film-pagination-button ${getButtonClasses(page)}`}
            />
          );
        })}
      </nav>
    </div>
  );
};

export default FilmPagination;